import React from 'react';
import css from '@styled-system/css';
import styled, { CSSObject } from 'styled-components';
import { compose, space, variant } from 'styled-system';

import A11yHidden from '@rover/kibble/styles/A11yHidden';
import {
  fontWeight,
  textAlign,
  textColor as textColorSystem,
} from '@rover/kibble/styles/styled-system';
import type { ThemeDisplaySizes } from '@rover/kibble/styles/themeTypes';
import roverTheme from '@rover/kibble/themes/roverTheme';

import Box from '../Box';
import { HeadingTags } from '../Heading/Heading.common';

import type { Props } from './Display.common';

export const DEFAULT_TAGS_FOR_SIZES: Record<ThemeDisplaySizes, HeadingTags> = {
  '400': 'h3',
  '500': 'h2',
  '600': 'h1',
};
export const getTag = (size: ThemeDisplaySizes, as?: HeadingTags): HeadingTags => {
  if (as) {
    return as;
  }

  return DEFAULT_TAGS_FOR_SIZES[size];
};
const StyledDisplay = styled(Box)<
  Omit<Props, 'as' | 'webProps' | 'rxnProps'> & {
    $a11yHidden: boolean;
    $responsive: boolean;
  }
>(
  // Resets values in case of global styles
  {
    padding: '0',
    margin: '0',
    border: '0 none',
    'font-weight': '400 !important', // prevent faux bolding in some browsers, have to use the dash-case version to avoid odd interactions with styled-system
  },
  (props) => {
    const display = props.theme?.display ? props.theme.display : roverTheme.display;
    const textColor = props.theme?.textColor ? props.theme.textColor : roverTheme.textColor;
    const displayResponsive = props.theme?.displayResponsive
      ? props.theme.displayResponsive
      : roverTheme.displayResponsive;
    return variant({
      prop: 'size',
      variants: {
        400: {
          color: textColor.display,
          fontFamily: `${display[400].fontFamily} !important`,
          fontSize: props.$responsive
            ? [display[400].fontSize, displayResponsive[400].fontSize]
            : display[400].fontSize,
          lineHeight: display[400].lineHeight,
          marginBottom: display[400].marginBottom,
        },
        500: {
          color: textColor.display,
          fontFamily: `${display[500].fontFamily} !important`,
          fontSize: props.$responsive
            ? [display[500].fontSize, displayResponsive[500].fontSize]
            : display[500].fontSize,
          lineHeight: display[500].lineHeight,
          marginBottom: display[500].marginBottom,
        },
        600: {
          color: textColor.display,
          fontFamily: `${display[600].fontFamily} !important`,
          fontSize: props.$responsive
            ? [display[600].fontSize, displayResponsive[600].fontSize]
            : display[600].fontSize,
          lineHeight: display[600].lineHeight,
          marginBottom: display[600].marginBottom,
        },
      },
    })(props);
  },
  (props) => (props.$a11yHidden ? `${A11yHidden}` : ''),
  compose(textAlign, textColorSystem, fontWeight, space), // Apply last to avoid being overwritten by resets and variant styles
  // support the "sx" prop.  This comes last so it will override any other styles
  (props) => css(props.sx)(props.theme) as CSSObject
);

function Display({
  webProps = {},
  size = '600',
  testID,
  // pull out rxnProps and do nothing with them because this is the web impl
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  rxnProps,
  ...other
}: Props): JSX.Element {
  const { a11yHidden = false, responsive = true, as, ...otherWebProps } = webProps;

  if (Array.isArray(size) && !as) {
    throw new Error('Display: `webProps.as` is required when `size` is a responsive array');
  }

  const resolvedSize = Array.isArray(size) ? size[0] : size;

  return (
    <StyledDisplay
      {...other}
      {...otherWebProps}
      as={getTag(resolvedSize, as)}
      size={size}
      data-testid={testID}
      $a11yHidden={a11yHidden}
      $responsive={responsive}
    />
  );
}

/**
 * Display is a large-format text component for prominent headings and hero text.
 * Uses larger typography than Heading with responsive sizing by default.
 *
 * READ THE DOCS: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/5211521289/Kibble+Core+Display
 */
export default Display;
